import * as React from 'react'
import { createRoot } from 'react-dom/client'
import type { Root } from 'react-dom/client'
import '../styles/index.css'

const componentMapping = {
  'hunt-achievements-card': () => import('../models/hunt-achievements/views/hunt-achievements-chart-view'),
  'react-test': () => import('../components/react-test'),
  'subscription-dashboard-card-view': () => import('../models/subscription/views/subscription-dashboard-card-view'),
  'user-scheduled-hunts-dashboard-card-view': () => import('../models/user-scheduled-hunts/views/user-scheduled-hunts-dashboard-card-view'),
  'user-scheduled-hunts-dashboard-view': () => import('../models/user-scheduled-hunts/views/user-scheduled-hunts-dashboard-view'),
  'concierge-dashboard-card-view': () => import('../models/tenant/views/concierge-dashboard-card-view'),
  'changelog-view': () => import('../models/cms/views/changelog-view'),
  'changelog-version-view': () => import('../models/cms/views/changelog-version-view'),
  'notes-dashboard': () => import('../models/notes/views/notes-dashboard-view'),
  'commission-calculator-view': () => import('../models/commissions/views/commission-calculator-view'),
  'commission-report-view': () => import('../models/commissions/views/commission-report-view'),
  'commission-overrides-view': () => import('../models/commission-overrides/views/commission-overrides-view'),
  'membership-renewals-view': () => import('../models/membership-renewals/views/membership-renewals-view'),
  'record-payments-view': () => import('../models/record-payments/views/record-payments-view'),
  'payment-plans-view': () => import('../models/payment-plans/views/payment-plans-view'),
  'advisor-renewals-view': () => import('../models/advisor-renewals/views/advisor-renewals-view'),
  'referral-partners-view': () => import('../models/referral-partners/views/referral-partners-view'),
  'outfitters-view': () => import('../models/outfitters/views/outfitters-view'),
  'referral-partners-report-view': () => import('../models/referral-partners/views/referral-partners-report-view'),
  'advisor-state-eligibilities-view': () => import('../models/advisor-state-eligibilities/views/advisor-state-eligibilities-view'),
  'quickbooks-transactions-view': () => import('../models/quickbooks-transactions/views/quickbooks-transactions-view'),
  'quickbooks-reconciliation-view': () => import('../models/quickbooks-reconciliation/views/quickbooks-reconciliation-view'),
  'oss-purchases-view': () => import('../models/oss-purchases/views/oss-purchases-view'),
  'oss-users-view': () => import('../models/oss-users/views/oss-users-view'),
  'out-of-date-hunts': () => import('../models/out-of-date-hunts/views/out-of-date-hunts-view'),
  'out-of-date-courses': () => import('../models/out-of-date-courses/views/out-of-date-courses-view'),
  'listing-changes-view': () => import('../models/listing-changes/views/listing-changes-view'),
  'dashboard-navbar': () => import('../models/app/views/dashboard-navbar-view'),
  'business-overview-view': () => import('../models/business-overview/views/business-overview-view'),
  'user-state-applications-view': () => import('../models/user-state-applications/views/user-state-applications-view'),
  'user-state-applications-create-view': () => import('../models/user-state-applications/views/user-state-applications-create-view'),
  'user-state-species-points-dashboard-card-view': () => import('../models/user-state-species-points/views/user-state-species-points-dashboard-card-view'),
  'hunt-plans-dashboard-card-view': () => import('../models/hunt-plans/views/hunt-plans-dashboard-card-view'),
  'user-roles': () => import('../models/user-roles/pages/user-roles'),
  'users-preview-view': () => import('../models/users-preview/views/users-preview-view'),
  'auth-preview-view': () => import('../models/auth-preview/views/auth-preview-view'),
  'admin-permissions-dashboard': () => import('../models/admin-tools/views/permissions-dashboard-view'),
  'user-state-species-points-cms': () => import('../models/user-state-species-points/views/user-state-species-points-cms-view'),
  'user-state-species-points-profile': () => import('../models/user-state-species-points/views/user-state-species-points-profile-view'),
  'external-state-site-mappings-cms': () => import('../models/external-state-site-mappings/views/external-state-site-mappings-cms-view'),
  'state-application-reminders-cms': () => import('../models/state-application-reminders/views/state-application-reminders-cms-view'),
  'hunt-eligibility-cms': () => import('../models/hunt-eligibility/views/hunt-eligibility-cms-view'),
  'hunt-eligible-users-view': () => import('../models/hunt-eligible-users/views/hunt-eligible-users-view'),
  'rep-hunt-qualifications-view': () => import('../models/rep-hunt-qualifications/views/rep-hunt-qualifications-view'),
  'rep-hunt-qualifications-dashboard-card-view': () => import('../models/rep-hunt-qualifications/views/rep-hunt-qualifications-dashboard-card-view'),
  'rep-hunt-plans-view': () => import('../models/rep-hunt-plans/views/rep-hunt-plans-view'),
  'rep-hunt-plans-dashboard-card-view': () => import('../models/rep-hunt-plans/views/rep-hunt-plans-dashboard-card-view'),
  'advisor-promotion-dashboard-card-view': () => import('../models/advisor-promotion/views/advisor-promotion-dashboard-card-view'),
  'advisor-commission-summary-dashboard-card-view': () => import('../models/advisor-commissions/views/advisor-commission-summary-dashboard-card-view'),
  'advisor-commissions-view': () => import('../models/advisor-commissions/views/advisor-commissions-view'),
  'global-toaster': () => import('../models/app/components/global-toaster'),
  'application-copier-view': () => import('../models/application-copier/views/application-copier-view'),
  'listing-filters-combobox': () => import('../models/listing-filters/components/listing-filters-combobox'),
  'listing-filters-text-input': () => import('../models/listing-filters/components/listing-filters-text-input'),
  'listing-filters-checkbox': () => import('../models/listing-filters/components/listing-filters-checkbox'),
  'agency-diagram-view': () => import('../models/agency-diagram/views/agency-diagram-view'),
  'storefront-view': () => import('../models/storefront/views/storefront-view'),
  'storefront-directory-view': () => import('../models/storefront/views/storefront-directory-view'),
} as const

const rootMap = new Map<Element, Root>()
let initializedBody: HTMLElement | null = null
let initializationVersion = 0

function unmountReactComponents() {
  initializedBody = null
  initializationVersion += 1
  rootMap.forEach((root) => {
    root.unmount()
  })
  rootMap.clear()
}

async function initializeReactComponents() {
  const body = document.body
  if (initializedBody === body) {
    return
  }

  unmountReactComponents()
  initializedBody = body
  const version = initializationVersion
  const mountPoints = body.querySelectorAll('[data-react-component]')

  for (const mountPoint of mountPoints) {
    const componentName = mountPoint.getAttribute('data-react-component') as keyof typeof componentMapping

    if (componentName && componentMapping[componentName]) {
      try {
        const { default: LoadedComponent } = await componentMapping[componentName]()
        if (version !== initializationVersion || document.body !== body || !mountPoint.isConnected) {
          return
        }
        const Component = LoadedComponent as React.ComponentType<Record<string, string | number>>

        const props: Record<string, string | number> = {}
        Array.from((mountPoint as Element).attributes).forEach((attr) => {
          if (attr.name.startsWith('data-') && attr.name !== 'data-react-component') {
            const propName = attr.name.replace('data-', '').replace(/-([a-z])/g, (_, letter) => letter.toUpperCase())
            const value = attr.value
            const numValue = Number(value)
            props[propName] = !Number.isNaN(numValue) && value.trim() !== '' ? numValue : value
          }
        })

        const root = createRoot(mountPoint as Element)
        rootMap.set(mountPoint, root)
        root.render(React.createElement(Component, props))
      }
      catch (error) {
        console.error(`Failed to load component: ${componentName}`, error)
      }
    }
  }
}

document.addEventListener('turbolinks:load', initializeReactComponents)
document.addEventListener('turbolinks:before-cache', unmountReactComponents)
document.addEventListener('turbolinks:before-render', unmountReactComponents)
